/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import {
  BadgePercent,
  Loader2,
  Plus,
  Power,
  PowerOff,
  ScrollText,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, EmptyState, StatusPill, fmtDateTime } from "./_shared";

const APPLIES_OPTIONS = [
  { value: "membership", label: "Membership" },
  { value: "archive", label: "Archive" },
  { value: "both", label: "Both" },
];

const blankForm = () => {
  const start = new Date();
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  return {
    code: "",
    description: "",
    percent_off: 100,
    duration_months: 3,
    applies_to: "membership",
    starts_at: start.toISOString().slice(0, 16),
    ends_at: end.toISOString().slice(0, 16),
    max_total_uses: 0,
    max_per_user: 1,
    active: true,
  };
};

function toIsoUtc(localStr) {
  // <input type="datetime-local"> gives e.g. "2026-02-12T08:30" (no tz).
  // Send as a real ISO 8601 UTC string.
  if (!localStr) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function AdminPromoCodes() {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [redemptionsFor, setRedemptionsFor] = useState(null); // {code, rows}
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setCodes(data.codes || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load promo codes.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        percent_off: Number(form.percent_off),
        duration_months: Number(form.duration_months),
        max_total_uses: Number(form.max_total_uses),
        max_per_user: Number(form.max_per_user),
        starts_at: toIsoUtc(form.starts_at),
        ends_at: toIsoUtc(form.ends_at),
      };
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo code ${payload.code} created`);
      setShowForm(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Could not create promo code.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onToggleActive = async (row) => {
    try {
      await api.patch(`/admin/promo-codes/${row.code_id}`, {
        active: !row.active,
      });
      toast.success(`${row.code} ${row.active ? "deactivated" : "activated"}`);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to update code.",
      );
    }
  };

  const onDelete = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete promo code "${row.code}"?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${row.code_id}`);
      toast.success(`${row.code} deleted`);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to delete code.",
      );
    }
  };

  const openRedemptions = async (row) => {
    setLoadingRedemptions(true);
    setRedemptionsFor({ code: row, rows: [] });
    try {
      const { data } = await api.get(
        `/admin/promo-codes/${row.code_id}/redemptions`,
      );
      setRedemptionsFor({ code: row, rows: data.redemptions || [] });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load redemptions.",
      );
      setRedemptionsFor(null);
    } finally {
      setLoadingRedemptions(false);
    }
  };

  return (
    <AdminLayout
      title="Promo codes"
      subtitle="Issue and manage launch / partner discount codes used at checkout."
      actions={
        <button
          type="button"
          onClick={() => {
            setForm(blankForm());
            setShowForm(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
          data-testid="promo-new-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New promo code
        </button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading promo codes…
        </div>
      ) : codes.length === 0 ? (
        <EmptyState
          title="No promo codes yet"
          body="Use the “New promo code” button above to create your first code."
          testid="promo-empty"
        />
      ) : (
        <Card testid="promo-table-card" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Applies to</th>
                  <th className="px-4 py-3">Window</th>
                  <th className="px-4 py-3">Usage</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {codes.map((c) => (
                  <tr
                    key={c.code_id}
                    data-testid={`promo-row-${c.code_id}`}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm font-semibold text-slate-900">
                        {c.code}
                      </div>
                      {c.description && (
                        <div className="text-xs text-slate-500 mt-0.5 max-w-xs">
                          {c.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <BadgePercent className="h-3.5 w-3.5 text-emerald-700" />
                        {c.percent_off}% off · {c.duration_months}{" "}
                        {c.duration_months === 1 ? "mo" : "mos"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <StatusPill
                        value={c.applies_to}
                        tone={c.applies_to === "both" ? "sky" : "slate"}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div>{fmtDateTime(c.starts_at)}</div>
                      <div>→ {fmtDateTime(c.ends_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      <div>
                        Used:{" "}
                        <span className="font-medium">
                          {c.used_count || 0}
                        </span>
                        {c.max_total_uses > 0 && (
                          <span className="text-slate-400">
                            {" "}/ {c.max_total_uses}
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500">
                        Per user: {c.max_per_user}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill
                        value={c.active ? "Active" : "Inactive"}
                        tone={c.active ? "green" : "slate"}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openRedemptions(c)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                          data-testid={`promo-redemptions-${c.code_id}`}
                          title="View redemptions"
                        >
                          <ScrollText className="h-3.5 w-3.5" /> Log
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleActive(c)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                          data-testid={`promo-toggle-${c.code_id}`}
                          title={c.active ? "Deactivate" : "Activate"}
                        >
                          {c.active ? (
                            <PowerOff className="h-3.5 w-3.5" />
                          ) : (
                            <Power className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(c)}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                          data-testid={`promo-delete-${c.code_id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showForm && (
        <PromoForm
          form={form}
          setForm={setForm}
          onCancel={() => setShowForm(false)}
          onSubmit={onCreate}
          submitting={submitting}
        />
      )}

      {redemptionsFor && (
        <RedemptionsDrawer
          data={redemptionsFor}
          loading={loadingRedemptions}
          onClose={() => setRedemptionsFor(null)}
        />
      )}
    </AdminLayout>
  );
}

function PromoForm({ form, setForm, onCancel, onSubmit, submitting }) {
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      data-testid="promo-form-modal"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-slate-900">
              Create promo code
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Codes are case-insensitive — they're stored uppercased.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-slate-500 hover:text-slate-700"
            data-testid="promo-form-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-700">Code</span>
            <input
              required
              type="text"
              value={form.code}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  code: e.target.value.toUpperCase(),
                }))
              }
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono uppercase"
              placeholder="BETA-2026"
              minLength={2}
              maxLength={64}
              data-testid="promo-form-code"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-700">
              Description (internal)
            </span>
            <input
              type="text"
              value={form.description}
              onChange={set("description")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="Launch promo — first 3 months free"
              maxLength={400}
              data-testid="promo-form-description"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Percent off
            </span>
            <input
              required
              type="number"
              min={1}
              max={100}
              value={form.percent_off}
              onChange={set("percent_off")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-percent"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              100 = free (no Stripe checkout).
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Duration (months)
            </span>
            <input
              required
              type="number"
              min={1}
              max={24}
              value={form.duration_months}
              onChange={set("duration_months")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-duration"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Applies to
            </span>
            <select
              value={form.applies_to}
              onChange={set("applies_to")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-applies"
            >
              {APPLIES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((f) => ({ ...f, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-slate-300"
              data-testid="promo-form-active"
            />
            <span className="text-xs text-slate-700">Active</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Starts at
            </span>
            <input
              required
              type="datetime-local"
              value={form.starts_at}
              onChange={set("starts_at")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-starts"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Ends at</span>
            <input
              required
              type="datetime-local"
              value={form.ends_at}
              onChange={set("ends_at")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-ends"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Max total uses
            </span>
            <input
              required
              type="number"
              min={0}
              value={form.max_total_uses}
              onChange={set("max_total_uses")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-max-total"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              0 = unlimited.
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Max per user
            </span>
            <input
              required
              type="number"
              min={1}
              max={100}
              value={form.max_per_user}
              onChange={set("max_per_user")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="promo-form-max-per-user"
            />
          </label>
        </div>
        <footer className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="promo-form-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="promo-form-submit"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Create promo code
          </button>
        </footer>
      </form>
    </div>
  );
}

function RedemptionsDrawer({ data, loading, onClose }) {
  const { code, rows } = data;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      data-testid="promo-redemptions-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[80vh] flex flex-col">
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-slate-900">
              Redemptions —{" "}
              <span className="font-mono">{code?.code}</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Append-only ledger. {rows.length} record
              {rows.length === 1 ? "" : "s"}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close"
            data-testid="promo-redemptions-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              No redemptions yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Final price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.redemption_id}>
                    <td className="px-4 py-2.5 text-xs text-slate-600">
                      {fmtDateTime(r.applied_at)}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-700">
                      {r.user_id}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <StatusPill value={r.plan} tone="slate" />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">
                      {r.percent_off}% × {r.duration_months}
                      {r.duration_months === 1 ? " mo" : " mos"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">
                      AUD ${r.final_price_aud?.toFixed?.(2) ?? r.final_price_aud}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
// End of AdminPromoCodes.jsx
